import { gql } from '@apollo/client';

// Gacha Shop (Surprise Boxes) admin documents. Every campaign query returns the full campaign so a
// save round-trips the canonical server state (updateGachaCampaign is a full-replace save) and the
// editor always holds the configVersion the next save must send back.

export const GACHA_MATCHER_FRAGMENT = gql`
  fragment GachaMatcherData on GachaMatcherConfig {
    userMinCreationDate
    userMaxCreationDate
    appVersionOperator
    appVersion
    userLevel
    subscriptionTier
    userIds
    spendTiers
    maestroOnly
    excludedUserIds
  }
`;

export const GACHA_POOL_ROW_FRAGMENT = gql`
  fragment GachaPoolRowData on GachaPoolRowConfig {
    reward {
      type
      itemId
      unlimitedBoostType
    }
    amountMode
    amount
    minAmount
    maxAmount
    weight
    chancePercent
  }
`;

export const GACHA_COUNT_ROW_FRAGMENT = gql`
  fragment GachaCountRowData on GachaCountRowConfig {
    count
    weight
    chancePercent
  }
`;

export const GACHA_CAMPAIGN_FRAGMENT = gql`
  fragment GachaCampaignData on GachaCampaign {
    id
    name
    enabled
    namespace
    startDate
    stopDate
    permanent
    showCountdown
    displayOrder
    matcher {
      ...GachaMatcherData
    }
    boxes {
      id
      tier
      type
      claimLimit
      refreshMode
      refreshIntervalSeconds
      price {
        currency
        amount
      }
      discount {
        percent
        startDate
        stopDate
      }
      countTable {
        ...GachaCountRowData
      }
      rewards {
        ...GachaPoolRowData
      }
      allowDuplicates
      reminderEnabled
      mystery {
        enabled
        matcher {
          inherit
          criteria {
            ...GachaMatcherData
          }
        }
        firstRatePercent
        decreasePercent
        minRatePercent
        recoveryMode
        recoveryIntervalSeconds
        pool {
          ...GachaPoolRowData
        }
      }
    }
    configVersion
    startNotificationEnabled
    startNotificationSentAt
    status
    deletable
    created
    updatedAt
    updatedBy
  }
  ${GACHA_MATCHER_FRAGMENT}
  ${GACHA_POOL_ROW_FRAGMENT}
  ${GACHA_COUNT_ROW_FRAGMENT}
`;

export const GACHA_SHOP_CONFIG_FRAGMENT = gql`
  fragment GachaShopConfigData on GachaShopConfig {
    enabled
    pinnedCampaignId
    pinnedBoxId
    updatedAt
  }
`;

export const GACHA_TEMPLATE_FRAGMENT = gql`
  fragment GachaTemplateData on GachaTemplate {
    id
    kind
    name
    countRows {
      ...GachaCountRowData
    }
    poolRows {
      ...GachaPoolRowData
    }
    updatedAt
  }
  ${GACHA_POOL_ROW_FRAGMENT}
  ${GACHA_COUNT_ROW_FRAGMENT}
`;

// The list query also carries the shop config: the kill switch banner and the pin location are
// shown on the list screen.
export const GET_GACHA_CAMPAIGNS = gql`
  query getGachaCampaigns {
    liveOps {
      gachaCampaigns {
        ...GachaCampaignData
      }
      gachaShopConfig {
        ...GachaShopConfigData
      }
    }
  }
  ${GACHA_CAMPAIGN_FRAGMENT}
  ${GACHA_SHOP_CONFIG_FRAGMENT}
`;

export const GET_GACHA_CAMPAIGN = gql`
  query getGachaCampaign($id: ID!) {
    liveOps {
      gachaCampaign(id: $id) {
        ...GachaCampaignData
      }
    }
  }
  ${GACHA_CAMPAIGN_FRAGMENT}
`;

export const GET_GACHA_SHOP_CONFIG = gql`
  query getGachaShopConfig {
    liveOps {
      gachaShopConfig {
        ...GachaShopConfigData
      }
    }
  }
  ${GACHA_SHOP_CONFIG_FRAGMENT}
`;

export const GET_GACHA_TEMPLATES = gql`
  query getGachaTemplates($kind: GachaTemplateKind) {
    liveOps {
      gachaTemplates(kind: $kind) {
        ...GachaTemplateData
      }
    }
  }
  ${GACHA_TEMPLATE_FRAGMENT}
`;

export const CREATE_GACHA_CAMPAIGN = gql`
  mutation createGachaCampaign($name: String!) {
    createGachaCampaign(name: $name) {
      ...GachaCampaignData
    }
  }
  ${GACHA_CAMPAIGN_FRAGMENT}
`;

export const UPDATE_GACHA_CAMPAIGN = gql`
  mutation updateGachaCampaign(
    $input: UpdateGachaCampaignInput!
    $expectedConfigVersion: Int!
    $confirmRunningEdit: Boolean
  ) {
    updateGachaCampaign(
      input: $input
      expectedConfigVersion: $expectedConfigVersion
      confirmRunningEdit: $confirmRunningEdit
    ) {
      ...GachaCampaignData
    }
  }
  ${GACHA_CAMPAIGN_FRAGMENT}
`;

export const SET_GACHA_CAMPAIGN_ORDER = gql`
  mutation setGachaCampaignOrder($ids: [ID!]!) {
    setGachaCampaignOrder(ids: $ids) {
      ...GachaCampaignData
    }
  }
  ${GACHA_CAMPAIGN_FRAGMENT}
`;

export const SET_GACHA_PINNED_BOX = gql`
  mutation setGachaPinnedBox($campaignId: ID, $boxId: ID) {
    setGachaPinnedBox(campaignId: $campaignId, boxId: $boxId) {
      ...GachaShopConfigData
    }
  }
  ${GACHA_SHOP_CONFIG_FRAGMENT}
`;

export const SET_GACHA_SHOP_ENABLED = gql`
  mutation setGachaShopEnabled($enabled: Boolean!) {
    setGachaShopEnabled(enabled: $enabled) {
      ...GachaShopConfigData
    }
  }
  ${GACHA_SHOP_CONFIG_FRAGMENT}
`;

export const CLONE_GACHA_CAMPAIGN = gql`
  mutation cloneGachaCampaign($id: ID!) {
    cloneGachaCampaign(id: $id) {
      ...GachaCampaignData
    }
  }
  ${GACHA_CAMPAIGN_FRAGMENT}
`;

export const DELETE_GACHA_CAMPAIGN = gql`
  mutation deleteGachaCampaign($id: ID!) {
    deleteGachaCampaign(id: $id)
  }
`;

export const SAVE_GACHA_TEMPLATE = gql`
  mutation saveGachaTemplate($input: SaveGachaTemplateInput!) {
    saveGachaTemplate(input: $input) {
      ...GachaTemplateData
    }
  }
  ${GACHA_TEMPLATE_FRAGMENT}
`;

export const DELETE_GACHA_TEMPLATE = gql`
  mutation deleteGachaTemplate($id: ID!) {
    deleteGachaTemplate(id: $id)
  }
`;
